import { getLocalImageSize } from '@/utils';
import { uploadFileList } from '@/utils/aliyunUpload';
import { localUpload } from '@/components/ImageSelect/service';
import { generateOriginalWatermarkedImage, generateThumbnail, generateWatermarkedThumbnail } from '@xmiles/holopix-helper'

/**
 * 将已加载的 Image 元素通过 Canvas 转为 JPG File（白色背景覆盖透明通道）
 */
function imageToJpgFile(img: HTMLImageElement, fileName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }
    // 白色背景填充，处理透明通道
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to convert to JPG'));
          return;
        }
        resolve(new File([blob], fileName, { type: 'image/jpeg' }));
      },
      'image/jpeg',
      0.92,
    );
  });
}

/**
 * 将 File 转为 JPG 格式（通过 Canvas 统一重编码，填充白色背景覆盖透明通道）
 *
 * NOTE: JPEG 不再短路直传——灰度（PIL mode=L）/CMYK JPEG、调色板 PNG 等非常规色彩模式
 * 若原样透传，ComfyUI LoadImage 读到非 RGB 数组（灰度为二维 (H,W)）会报
 * IndexError: tuple index out of range；canvas 的 JPEG 输出必为 RGB 三通道，统一重编码兜底。
 */
export function convertFileToJpg(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.src = url;

    img.onload = () => {
      URL.revokeObjectURL(url);
      const name = file.name.replace(/\.[^.]+$/, '.jpg');
      // 仅重编码异常时回退原文件（保持旧直传可用性）；解码失败仍 reject 由调用方拦截
      imageToJpgFile(img, name).then(resolve).catch(() => resolve(file));
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for JPG conversion'));
    };
  });
}

/**
 * 将图片 URL 转为 JPG 格式（通过 Canvas 转换，白色背景覆盖透明通道）
 */
export function convertUrlToJpg(imageUrl: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    img.onload = () => imageToJpgFile(img, 'converted.jpg').then(resolve).catch(reject);

    img.onerror = () => reject(new Error('Failed to load image for JPG conversion'));
  });
}

// 获取压缩的图片地址
type ImageFormat = 'webp' | 'png';
export function getImageZipUrl(url?: string, { format = 'webp' as ImageFormat, width = 100, quantity = 90 } = {}) {
  return generateThumbnail(url!, {width: width * 2});
}

// 获取七牛图片水印地址
interface WaterMaskOptions {
  width?: number;
}
export function getImageWaterMask(url: string, options?: WaterMaskOptions) {
  if (options?.width) {
    return generateWatermarkedThumbnail(url, { width: options.width });
  }
  return generateOriginalWatermarkedImage(url);
}

export function imageUseMask(type?: number) {
  if (!type) {
    return false;
  }
  return [1, 3].includes(type);
}

// 判断图片是否全部为黑色像素
function isImageAllBlack(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // 使用示例
    const img = new Image();
    img.crossOrigin = "";
    img.src = url;

    img.onload = () => {
      if (ctx) {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        let isAllBlack = true;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] !== 0 || data[i + 1] !== 0 || data[i + 2] !== 0) {
            isAllBlack = false;
            break;
          }
        }

        resolve(isAllBlack);
      } else {
        resolve(false);
      }
    };

    img.onerror = () => {
      resolve(false);
    };
  });
}

// 检测图片是否有透明色
export function imageHasTransparent(imgUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = "";
    img.src = imgUrl;

    img.onload = () => {
      if (ctx) {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let data = imageData.data;
        let hasTransparent = false;

        // 分析像素
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];

          // 假设完全透明的像素为白色
          if (alpha === 0) {
            hasTransparent = true;
            break;
          }
        }

        resolve(hasTransparent);
      }
    }

    img.onerror = () => {
      resolve(false);
    };
  })
}

import { base64ToFile, downloadFile } from './index';
import { uploadFilePromise } from '@/utils/aliyunUpload';
import { defaultCheckCompletion, Poller } from './QuickTaskPoller';
import { faceDetection, superImg } from '@/views/workspace/service';
import { useDownloadImageStore } from '@/store/downloadImage';
// 将图片透明像素替换成白色像素
export function handleTransparentLayerWhite(imgUrl: string): Promise<number | null> {
  return new Promise(async (resolve) => {
    if (typeof imgUrl !== 'string') {
      return resolve(null);
    }

    const hasTransparent = await imageHasTransparent(imgUrl);

    if (!hasTransparent) {
      return resolve(null);
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = "";
    img.src = imgUrl;

    img.onload = async () => {
      if (ctx) {
        canvas.width = img.width;
        canvas.height = img.height;

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, img.width, img.height);
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        ctx.putImageData(imageData, 0, 0);
        const file = base64ToFile(canvas.toDataURL());

        const url = await uploadFilePromise(file);
        const { data } = await localUpload({
          type: 4,
          image: [{
            width: img.width,
            height: img.height,
            image: url,
            size: file.size
          }]
        });
        resolve(data[0].id);
      }
    }
  });
}


// 将 base64 数据转换为 File 对象
export function base64ToFileV2(base64String: string, fileName: string = 'image_with_background.png'): File {
  // 移除 base64 URL 的头部信息
  const byteString = atob(base64String.split(',')[1]);
  const mimeType = base64String.split(',')[0].split(':')[1].split(';')[0];

  // 创建一个 Uint8Array 来处理二进制数据
  const arrayBuffer = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    arrayBuffer[i] = byteString.charCodeAt(i);
  }

  // 创建 Blob 对象
  const blob = new Blob([arrayBuffer], { type: mimeType });

  // 将 Blob 转换为 File 对象
  return new File([blob], fileName, { type: mimeType });
}

// 将图片等比缩放并填充到1024*1024尺寸
export function resizeImageTo1024(
  imageUrl: string,
  backgroundColor?: string
): Promise<{ file: File; width: number; height: number; sizeBytes: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;
      const targetSize = 1024;

      // 创建1024*1024的canvas
      const canvas = document.createElement('canvas');
      canvas.width = targetSize;
      canvas.height = targetSize;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('无法获取Canvas 2D上下文'));
        return;
      }

      // 计算等比缩放后的尺寸
      const scale = Math.min(targetSize / originalWidth, targetSize / originalHeight);
      const scaledWidth = originalWidth * scale;
      const scaledHeight = originalHeight * scale;

      // 计算居中位置
      const offsetX = (targetSize - scaledWidth) / 2;
      const offsetY = (targetSize - scaledHeight) / 2;

      // 如果提供了背景色，先填充背景
      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, targetSize, targetSize);
      } else {
        // 默认使用白色背景
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetSize, targetSize);
      }

      // 绘制等比缩放后的图片
      ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

      // 转换为base64
      const resultUrl = canvas.toDataURL('image/png');
      const sizeBytes = calculateBase64SizeBytes(resultUrl);
      const file = base64ToFileV2(resultUrl, 'resized_1024x1024.png');

      resolve({
        file,
        width: targetSize,
        height: targetSize,
        sizeBytes
      });
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = imageUrl;
  });
}

// 将图片等比缩放到指定像素总数（约1024*1024=1048576像素）
export function resizeImageToPixelCount(
  imageUrl: string,
  targetPixelCount: number = 1048576, // 1024*1024
  backgroundColor?: string
): Promise<{ file: File; width: number; height: number; sizeBytes: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const originalWidth = img.width;
      const originalHeight = img.height;
      const originalPixelCount = originalWidth * originalHeight;

      // 如果原图像素数已经接近目标像素数（±10%），则不需要调整
      if (Math.abs(originalPixelCount - targetPixelCount) / targetPixelCount <= 0.1) {
        // 创建canvas保持原尺寸
        const canvas = document.createElement('canvas');
        canvas.width = originalWidth;
        canvas.height = originalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法获取Canvas 2D上下文'));
          return;
        }

        // 如果提供了背景色，先填充背景
        if (backgroundColor) {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, originalWidth, originalHeight);
        }

        // 绘制原图
        ctx.drawImage(img, 0, 0);

        // 转换为base64
        const resultUrl = canvas.toDataURL('image/png');
        const sizeBytes = calculateBase64SizeBytes(resultUrl);
        const file = base64ToFileV2(resultUrl, 'resized_image.png');

        resolve({
          file,
          width: originalWidth,
          height: originalHeight,
          sizeBytes
        });
        return;
      }

      // 计算缩放比例
      const scale = Math.sqrt(targetPixelCount / originalPixelCount);
      const newWidth = Math.round(originalWidth * scale);
      const newHeight = Math.round(originalHeight * scale);

      // 创建canvas
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('无法获取Canvas 2D上下文'));
        return;
      }

      // 如果提供了背景色，先填充背景
      if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, newWidth, newHeight);
      } else {
        // 默认使用白色背景
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, newWidth, newHeight);
      }

      // 绘制等比缩放后的图片
      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      // 转换为base64
      const resultUrl = canvas.toDataURL('image/png');
      const sizeBytes = calculateBase64SizeBytes(resultUrl);
      const file = base64ToFileV2(resultUrl, 'resized_image.png');

      resolve({
        file,
        width: newWidth,
        height: newHeight,
        sizeBytes
      });
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = imageUrl;
  });
}

// 添加背景色到图片
export function addBackgroundToImage(
  imageUrl: string,
  backgroundColor: string,
  callback: (file: File, width: number, height: number, sizeBytes: number) => void
): void {
  const img = new Image();
  img.crossOrigin = 'anonymous'; // 解决跨域问题，如果图片在其他域名

  img.onload = () => {
    const width = img.width;
    const height = img.height;

    // 创建一个 canvas 元素
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    if (ctx) {
      // 设置背景颜色
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);

      // 绘制图片
      ctx.drawImage(img, 0, 0);

      // 将处理后的图片转换为 Data URL（base64 格式）
      const resultUrl = canvas.toDataURL('image/png');

      // 计算图片的字节大小
      const sizeBytes = calculateBase64SizeBytes(resultUrl);

      // 将 base64 数据转换为 Blob
      const file = base64ToFileV2(resultUrl);

      // 回调函数返回结果
      callback(file, width, height, sizeBytes);
    }
  };

  img.src = imageUrl; // 设置图片的 URL
}

export function addBackgroundToImagePs(
  imageUrl: string,
  backgroundColor: string
): Promise<{ file: File; width: number; height: number; sizeBytes: number }> {
  return new Promise((resolve, reject) => {
    const mimeType = imageUrl.split(',')[0].split(':')[1].split(';')[0];

    const img = new Image();
    img.crossOrigin = 'anonymous'; // 解决跨域问题，如果图片在其他域名

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      if (['image/gif','image/webp'].includes(mimeType)) {
        // For GIFs, we don't add a background to preserve the animation.
        // We just convert the base64 back to a file and return it.
        const sizeBytes = calculateBase64SizeBytes(imageUrl);
        const file = base64ToFileV2(imageUrl, 'image.gif');
        resolve({ file, width, height, sizeBytes });
        return;
      }

      // 创建一个 canvas 元素
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      if (ctx) {
        // 设置背景颜色
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);

        // 绘制图片
        ctx.drawImage(img, 0, 0);

        // 将处理后的图片转换为 Data URL（base64 格式），保持原始格式
        const resultUrl = canvas.toDataURL(mimeType || 'image/png');

        // 计算图片的字节大小
        const sizeBytes = calculateBase64SizeBytes(resultUrl);

        // 从原始URL中提取文件名和扩展名
        const fileExtension = (mimeType || 'image/png').split('/')[1];
        const fileName = `image_with_background.${fileExtension}`;

        // 将 base64 数据转换为 Blob，保持原始MIME类型
        const file = base64ToFileV2(resultUrl, fileName);

        // 解析 Promise，返回结果
        resolve({ file, width, height, sizeBytes });
      } else {
        // 无法获取 canvas 上下文，拒绝 Promise
        reject(new Error('Failed to get 2D context'));
      }
    };

    img.onerror = (error) => {
      // 处理图片加载错误
      reject(error);
    };

    img.src = imageUrl; // 设置图片的 URL
  });
}

// 计算 base64 数据的字节大小（B）
export function calculateBase64SizeBytes(base64String: string): number {
  // 移除 base64 URL 的头部信息，如 "data:image/png;base64,"
  const base64Data = base64String.split(',')[1];

  // base64 的大小公式是 (3/4) * 字符数，减去 padding 的 "="
  const padding = (base64Data.match(/=+$/) || []).length;
  const sizeInBytes = (base64Data.length * 3) / 4 - padding;

  return sizeInBytes;
}


type RGBColor = {
  r: number;
  g: number;
  b: number;
};

// 辅助函数：计算两个颜色之间的距离
function colorDistance(color1: RGBColor, color2: RGBColor): number {
  return Math.sqrt(
    Math.pow(color1.r - color2.r, 2) +
    Math.pow(color1.g - color2.g, 2) +
    Math.pow(color1.b - color2.b, 2)
  );
}

// 主函数：计算颜色占比
export // 主函数：计算颜色占比，忽略透明背景
  function getColorPercentageInImage(targetColor: RGBColor, imageUrl: string, tolerance: number = 30, alphaThreshold: number = 128): Promise<number> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous'; // 避免跨域问题
    img.src = imageUrl;

    img.onload = () => {
      // 创建 canvas 元素
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject('无法获取 Canvas 2D 上下文');
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // 获取图像数据
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      const data = imageData.data;

      let matchingPixelCount = 0;
      let validPixelCount = 0;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3]; // alpha 值

        // 如果 alpha 值低于阈值，忽略该像素
        if (a < alphaThreshold) {
          continue;
        }

        validPixelCount++;

        const currentColor: RGBColor = { r, g, b };

        // 计算当前像素颜色与目标颜色的距离
        if (colorDistance(currentColor, targetColor) <= tolerance) {
          matchingPixelCount++;
        }
      }

      // 计算目标颜色在有效像素中的占比
      const percentage = (matchingPixelCount / validPixelCount) * 100;
      resolve(percentage);
    };

    img.onerror = () => {
      reject('加载图片时出错');
    };
  });
}


// 检测黑色是否超过 50%
export function isBlackMoreThan50Percent(imageUrl: string): Promise<boolean> {
  const blackColor: RGBColor = { r: 0, g: 0, b: 0 };
  const tolerance = 40; // 宽松的黑色匹配容差
  return getColorPercentageInImage(blackColor, imageUrl, tolerance).then((percentage) => {
    return percentage > 50;
  });
}

// 检测白色是否超过 50%
export function isWhiteMoreThan50Percent(imageUrl: string): Promise<boolean> {
  const whiteColor: RGBColor = { r: 255, g: 255, b: 255 };
  const tolerance = 40; // 宽松的白色匹配容差
  return getColorPercentageInImage(whiteColor, imageUrl, tolerance).then((percentage) => {
    return percentage > 50;
  });
}

// 将file转换成图片
export function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject('加载图片时出错');
    };
    img.src = URL.createObjectURL(file);
  });
}

// 添加自定义水印
export async function addCustomWatermark(imageUrl: string, { watermark = '由HolopixAI生成' } = {}): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = imageUrl;
    img.crossOrigin = '';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext("2d")!;

      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);

      // 设置水印样式
      ctx.font = '20px sans-serif';  // 字体大小
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';  // 白色，半透明
      ctx.textAlign = 'center';  // 水平居中
      ctx.textBaseline = 'middle';  // 垂直居中

      // 计算水印的位置
      const text = watermark;
      const textWidth = ctx.measureText(text).width;

      // 设置水印的初始坐标
      let x = 0;
      let y = 0;

      // 绘制水印，填满整个画布
      const gap = 100; // 水印之间的间距

      // 计算旋转角度（45度）
      const angle = -14 * Math.PI / 180;  // 转换为弧度

      for (y = 0; y < canvas.height; y += gap) {
        for (x = 0; x < canvas.width; x += gap + textWidth) {
          // ctx.fillText(text, x + textWidth / 2, y + 24);  // 绘制水印
          // 设置旋转中心为当前水印坐标（x, y）
          ctx.save();  // 保存当前状态
          ctx.translate(x + textWidth / 2, y + 24);  // 移动到文字中心
          ctx.rotate(angle);  // 旋转画布
          ctx.fillText(text, 0, 0);  // 绘制水印
          ctx.restore();  // 恢复之前保存的状态
        }
      }

      resolve(canvas.toDataURL());
    }
  });
}

// 定义响应接口
interface FaceDetectionResponse {
  msg: string;
  code: number;
  data: {
    imgUrl: string | null;
    imgId: string | null;
    status: number;
    requestId: string;
    msg: string | null;
    face?: number; // 0 表示没有检测到人脸，1 表示检测到人脸
  };
  success: boolean;
}

/**
 * 人脸检测
 * @param params 
 * @returns 
 */
export const handleFaceDetection = async (params: any): Promise<FaceDetectionResponse | undefined> => {
  const poller = new Poller<FaceDetectionResponse>({
    shouldLog: true,
    logMessage: `耗时计算-人脸检测`,
  });

  try {
    const finalResult = await poller
      .on('complete', (result: any) => {
        console.log('人脸检测完成:', result);
      })
      .on('error', (error: any) => {
        console.error('人脸检测错误:', error);
      })
      .poll(
        () => faceDetection(params),
        defaultCheckCompletion
      );

    return finalResult;
  } catch (error) {
    console.error('轮询失败:', error);
    throw error;
  }
};


import { useUserStore } from '@/store/user';
/**
 * 图片放大
 */
export const handleSuperImg = async (params: any): Promise<FaceDetectionResponse | undefined> => {
  const poller = new Poller<FaceDetectionResponse>({
    shouldLog: true,
    logMessage: `耗时计算-图片放大-${params.rescaleFactor}`,
  });
  const downloadImageStore = useDownloadImageStore();
  // 是否需要更新算力
  let emitPowerUpdated = false;
  const userStore = useUserStore();
  downloadImageStore.updateDownload('generating');

  try {
    const finalResult = await poller
      .on('complete', async (result: any) => {
        downloadImageStore.updateDownload('generateSuccess');
        return result;
      })
      .on('error', (error: any) => {
        console.error('图片放大错误:', error);
        userStore.queryUserRights();
        downloadImageStore.updateDownload('generateError');
        throw error;
      })
      .on('poll', () => {
        if (!emitPowerUpdated) {
          emitPowerUpdated = true;
          // 通知更新算力
          userStore.queryUserRights();
        }
      })
      .poll(
        () => superImg(params),
        defaultCheckCompletion
      );

    return finalResult;
  } catch (error) {
    console.error('轮询失败:', error);
    throw error;
  }
}

export function validateFileType(file: File): Promise<boolean> {
  return new Promise((resolve) => {
    const validTypes = {
      'image/jpeg': [0xFF, 0xD8, 0xFF], // JPEG  
      'image/png': [0x89, 0x50, 0x4E, 0x47], // PNG  
      'image/webp': [0x52, 0x49, 0x46, 0x46], // WebP RIFF  
    };


    const reader = new FileReader();
    reader.onload = function (event) {
      if (!event.target?.result) return resolve(false);
      const result = event.target.result as ArrayBuffer;
      const bytes = new Uint8Array(result);

      // WebP 文件需要特殊处理  
      if (file.type === 'image/webp') {
        const webpSignature = [0x52, 0x49, 0x46, 0x46]; // RIFF  
        const webpFormat = [0x57, 0x45, 0x42, 0x50]; // WEBP  

        // 检查 RIFF 头  
        const isRiff = webpSignature.every((byte, index) => bytes[index] === byte);
        // 检查 WEBP 标识（从第 12 个字节开始）  
        const isWebp = webpFormat.every((byte, index) => bytes[index + 8] === byte);

        resolve(isRiff && isWebp);
        return;
      }


      // 🔍 特殊处理：GIF（完整 6 字节校验）
      if (file.type === 'image/gif') {
        const gifHeader = [0x47, 0x49, 0x46]; // "GIF"
        const gifVersions = [
          [0x38, 0x37, 0x61], // "87a"
          [0x38, 0x39, 0x61]  // "89a"
        ];
        // 校验前 3 字节是否为 "GIF"
        const isGifHeader = gifHeader.every((byte, i) => bytes[i] === byte);
        // 校验后 3 字节是否为 "87a" 或 "89a"
        const isVersionValid = gifVersions.some(version => 
          version.every((vByte, j) => bytes[j + 3] === vByte)
        );
        resolve(isGifHeader && isVersionValid);
        return;
      }

      // 其他图片格式检查  
      for (const type of Object.keys(validTypes) as Array<keyof typeof validTypes>) {
        const magicNumbers = validTypes[type];
        if (bytes.slice(0, magicNumbers.length).every((byte, index) => byte === magicNumbers[index])) {
          resolve(true);
          return;
        }
      }
      resolve(false); // 未找到有效类型  
    };

    reader.onerror = function (error) {
      console.error("文件读取错误:", error);
      resolve(false); // 读取文件出错  
    };

    reader.readAsArrayBuffer(file);
  });
}

/**
 * 当用户上传大于1:6比例的图片，前端做个校验阻止用户上传并弹出toast：您上传的图片宽高比过高，请调整为1:6以内。文俊
 */
export function checkImageRatio(file: File, ratio: number = 6): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      // 判断图片是横向还是纵向
      const isHorizontal = img.width > img.height;
      resolve(isHorizontal ? img.height / img.width > (1 / ratio) : img.width / img.height > (1 / ratio));
    };
    img.onerror = () => {
      // 无法作为图片加载（如视频文件），返回 true 不拦截
      // 让类型检查去拦截非图片文件
      resolve(true);
    };
  });
}

import type { ImageCommonState, LayoutReferState } from '@/types/image';
import { ModuleEnum } from '@/assets/artwork/moduleEnum';
// 等比压缩图片指指定像素内
export async function compressImage(
  imageState: LayoutReferState,
  maxArea = 2000 * 2000
): Promise<LayoutReferState> {

  if (imageState.width * imageState.height <= maxArea) {
    return imageState;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // 处理跨域问题
    img.src = imageState.image;

    img.onload = async () => {
      let { width, height } = img;

      // 计算图片面积
      let area = width * height;

      // 如果面积超过最大值，按比例缩小
      if (area > maxArea) {
        const scaleFactor = Math.sqrt(maxArea / area);
        width = Math.floor(width * scaleFactor);
        height = Math.floor(height * scaleFactor);
      }

      // 创建 canvas 进行压缩
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      ctx!.drawImage(img, 0, 0, width, height);

      // 将处理后的图片转换为 Data URL（base64 格式）
      const resultUrl = canvas.toDataURL('image/png');

      // 将 base64 数据转换为 Blob
      const file = base64ToFileV2(resultUrl);

      const url = await uploadFilePromise(file);

      const { data } = await localUpload({
        type: 4,
        image: [
          {
            width,
            height,
            image: url,
            size: file.size
          }
        ]
      });

      resolve({
        id: data[0].id,
        width,
        height,
        image: url,
      });
    };

    img.onerror = (err) => reject(err);
  });
}

/**
 * 预加载图片
 * @param {string[]} urls - 图片URL数组
 * @param {number} [concurrency=6] - 并发数（默认6，适配浏览器同域限制）
 */
export async function preloadImages(urls: string[], concurrency = 6) {
  const total = urls.length;
  for (let i = 0; i < total; i += concurrency) {
    const batch = urls.slice(i, i + concurrency);
    await Promise.allSettled(
      batch.map(url =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = resolve;  // 加载成功
          img.onerror = () => reject(`加载失败: ${url}`); // 失败记录URL
          img.src = url;
        })
      )
    );
  }
}


/**
   * 计算符合 object-fit: contain 效果的适配尺寸
   * @param {Object} container - 容器尺寸 { width: number, height: number }
   * @param {Object} content - 内容原始尺寸 { width: number, height: number }
   * @returns {Object} 适配后的尺寸和缩放比例 { width: number, height: number, scale: number }
 */
export function objectFitContain(container: { width: number; height: number; }, content: { width: number; height: number; }) {
  // 参数校验
  if (container.width <= 0 || container.height <= 0) {
    throw new Error("容器尺寸必须为正值");
  }
  if (content.width <= 0 || content.height <= 0) {
    throw new Error("内容尺寸必须为正值");
  }

  // 计算宽高比
  const containerRatio = container.width / container.height;
  const contentRatio = content.width / content.height;

  // 计算适配逻辑
  let width, height, scale;
  // 根据宽高比决定缩放策略:
  // 1. 如果内容区域比图片更宽(contentRatio > imgRatio):
  //    - 以高度为基准进行缩放
  //    - 新高度 = contentHeight
  //    - 新宽度 = contentHeight * imgRatio (保持原始比例)
  // 2. 如果内容区域比图片更窄:
  //    - 以宽度为基准进行缩放
  //    - 新宽度 = contentWidth
  //    - 新高度 = contentWidth / imgRatio (保持原始比例)
  if (containerRatio > contentRatio) {
    // 高度适配模式：内容高度撑满容器，宽度按比例缩放
    height = container.height;
    width = height * contentRatio;
    scale = height / content.height;
  } else {
    // 宽度适配模式：内容宽度撑满容器，高度按比例缩放
    width = container.width;
    height = width / contentRatio;
    scale = width / content.width;
  }

  return {
    width,
    height,
    scale: Number(scale.toFixed(4))  // 保留4位小数避免浮点误差
  };
}


/**
 * 计算图像的宽高
 * @param {string} ratio - 宽高比例，例如 "16:9"
 * @param {number} totalArea - 总面积，单位为像素的平方
 * @returns {Object} 计算后的宽高和面积
*/
export function calculateDimensions(ratio: any, totalArea: number) {
  // 分解比例，例如 "16:9" 或者直接传入一个数字如 16/9
  let [widthRatio, heightRatio] = typeof ratio === 'string'
    ? ratio.split(':').map(Number)
    : [ratio, 1];

  // 如果 ratio 是分数形式（例如 16/9）
  if (!heightRatio) {
    heightRatio = 1;
    widthRatio = ratio;
  }

  // 计算比例的商值
  const ratioValue = widthRatio / heightRatio;

  // 根据公式推导：面积 = 宽 * 高，宽/高 = ratioValue
  // 因此：宽 = ratioValue * 高，面积 = ratioValue * 高 * 高
  // 推导出：高 = sqrt(面积 / ratioValue)，宽 = ratioValue * 高
  const height = Math.round(Math.sqrt(totalArea / ratioValue));
  const width = Math.round(height * ratioValue);

  // 返回结果
  return {
    width: width,
    height: height,
    area: width * height,
    originalRatio: ratioValue,
    actualRatio: width / height
  };
}

/**
 * 将带透明的图片转换为不带透明的图片，并上传到图床
 */
export async function convertTransparentImageToOpaque(img: ImageCommonState) {
  if (!img.image) {
    return null;
  }
  // 检查图片是否有透明像素
  const hasTransparent = await imageHasTransparent(img.image);
  if (!hasTransparent) {
    return img;
  }

  // 检查图片是否大部分为白色
  const isWhite = await isWhiteMoreThan50Percent(img.image);
  // 添加背景色
  const { file,  sizeBytes } = await addBackgroundToImagePs(
    img.image,
    isWhite ? '#000000' : '#FFFFFF'
  );

  // 获取图片尺寸
  const imageSize = await getLocalImageSize(file);
  
  // 上传文件到图床
  const [uploadedUrl] = await uploadFileList([file]);

  // 上传到本地存储
  const { data } = await localUpload({
    type: 5,
    image: [{
      width: imageSize.width,
      height: imageSize.height,
      image: uploadedUrl,
      size: sizeBytes
    }]
  });

  return data[0];
}


/**
 * 获取预览图，超清放大使用上传图
 */
export const getGeneratorPreviewImage = (imageUrl: string, param: string) => {

  try {
    const params = typeof param === 'string' ? JSON.parse(param) : param;

    if (params.module === ModuleEnum.zoom) {
      return params.originImgUrl;
    }

    return imageUrl;
  } catch (error) {
    return imageUrl;
  }
};
